import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import {Button} from '../../../components/ui/Button';
import apiService from '../../../services/api';

const PrivacyControls = () => {
  const [settings, setSettings] = useState({
    profileVisibility: 'friends', // 'public', 'friends', 'private'
    allowEventInvites: true,
    showInSearch: true,
    shareStatistics: false,
    allowDataAnalytics: true,
    emailNotifications: true,
    activityStatus: true
  });

  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await apiService.request('/users/profile');
        if (response.success) {
          setSettings(prev => ({
            ...prev,
            profileVisibility: response.data.user.profileVisibility || 'friends',
            allowEventInvites: response.data.user.allowEventInvites ?? true,
            showInSearch: response.data.user.showInSearch ?? true,
            shareStatistics: response.data.user.shareStatistics ?? false,
            allowDataAnalytics: response.data.user.allowDataAnalytics ?? true,
            emailNotifications: response.data.user.emailNotifications ?? true,
            activityStatus: response.data.user.activityStatus ?? true
          }));
        } else {
          throw new Error(response.message || 'Failed to fetch settings');
        }
      } catch (err) {
        setError(err.message);
        console.error('Error fetching settings:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveSettings = async () => {
    setIsLoading(true);
    try {
      const response = await apiService.updateSettings(settings);
      if (response.success) {
        // Update local storage with new settings
        const currentUser = apiService.getStoredUser();
        const updatedUser = { ...currentUser, ...settings };
        apiService.storeUser(updatedUser);
        alert('Privacy settings updated successfully!');
      } else {
        throw new Error(response.message || 'Failed to update settings');
      }
    } catch (error) {
      console.error('Privacy settings update error:', error);
      alert('Failed to update privacy settings: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const visibilityOptions = [
    {
      value: 'public',
      label: 'Public',
      description: 'Anyone can see your profile and add you to events',
      icon: 'Globe'
    },
    {
      value: 'friends',
      label: 'Friends Only',
      description: 'Only people you\'ve shared expenses with can see your profile',
      icon: 'Users'
    },
    {
      value: 'private',
      label: 'Private',
      description: 'Your profile is hidden from search and discovery',
      icon: 'Lock'
    }
  ];

  const privacyControls = [
    {
      key: 'allowEventInvites',
      title: 'Allow Event Invitations',
      description: 'Let others invite you to expense events',
      icon: 'Calendar'
    },
    {
      key: 'showInSearch',
      title: 'Show in People Search',
      description: 'Allow others to find you when searching for people',
      icon: 'Search'
    },
    {
      key: 'shareStatistics',
      title: 'Share Statistics',
      description: 'Allow your expense statistics to be visible to friends',
      icon: 'BarChart'
    },
    {
      key: 'allowDataAnalytics',
      title: 'Anonymous Analytics',
      description: 'Help improve ExpenseLoop by sharing anonymous usage data',
      icon: 'TrendingUp'
    },
    {
      key: 'emailNotifications',
      title: 'Email Notifications',
      description: 'Receive notifications about expense activities via email',
      icon: 'Mail'
    },
    {
      key: 'activityStatus',
      title: 'Show Activity Status',
      description: 'Let friends see when you were last active',
      icon: 'Activity'
    }
  ];

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="Shield" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Privacy Controls</h3>
              <p className="text-sm text-muted-foreground">
                Manage your privacy and sharing preferences
              </p>
            </div>
          </div>
        </div>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="Shield" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Privacy Controls</h3>
              <p className="text-sm text-muted-foreground">
                Manage your privacy and sharing preferences
              </p>
            </div>
          </div>
        </div>
        <div className="text-center py-8 text-muted-foreground">
          <p>Unable to load privacy settings</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="Shield" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Privacy Controls</h3>
            <p className="text-sm text-muted-foreground">
              Manage your privacy and sharing preferences
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Profile Visibility */}
        <div>
          <h4 className="text-md font-medium text-foreground mb-3">Profile Visibility</h4>
          <div className="space-y-3">
            {visibilityOptions?.map((option) => (
              <label
                key={option?.value}
                className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-all ${
                  settings?.profileVisibility === option?.value
                    ? 'border-primary bg-primary/5' :'border-border hover:bg-muted'
                }`}
              >
                <input
                  type="radio"
                  name="profileVisibility"
                  value={option?.value}
                  checked={settings?.profileVisibility === option?.value}
                  onChange={(e) => handleSettingChange('profileVisibility', e?.target?.value)}
                  className="h-4 w-4 text-primary focus:ring-ring border-border"
                />
                <div className="flex items-center space-x-3 flex-1">
                  <div className="p-2 bg-muted rounded-lg">
                    <Icon name={option?.icon} size={16} className="text-muted-foreground" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground">{option?.label}</div>
                    <div className="text-xs text-muted-foreground">{option?.description}</div>
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Privacy Settings */}
        <div>
          <h4 className="text-md font-medium text-foreground mb-3">Privacy Settings</h4>
          <div className="space-y-4">
            {privacyControls?.map((control) => (
              <div key={control?.key} className="flex items-start justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-background rounded-lg mt-0.5">
                    <Icon name={control?.icon} size={16} className="text-muted-foreground" />
                  </div>
                  <div>
                    <h5 className="text-sm font-medium text-foreground">{control?.title}</h5>
                    <p className="text-xs text-muted-foreground">{control?.description}</p>
                  </div>
                </div>
                <label className="inline-flex items-center mt-1">
                  <input
                    type="checkbox"
                    checked={settings?.[control?.key]}
                    onChange={(e) => handleSettingChange(control?.key, e?.target?.checked)}
                    className="h-4 w-4 text-primary focus:ring-ring border-border rounded"
                  />
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Privacy Actions */}
        <div>
          <h4 className="text-md font-medium text-foreground mb-3">Data Management</h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 border border-border rounded-lg">
              <div>
                <h5 className="text-sm font-medium text-foreground">Download Your Data</h5>
                <p className="text-xs text-muted-foreground">
                  Get a copy of all your ExpenseLoop data in JSON format
                </p>
              </div>
              <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
                Export
              </Button>
            </div>

            <div className="flex items-center justify-between p-3 border border-border rounded-lg">
              <div>
                <h5 className="text-sm font-medium text-foreground">Delete Account Data</h5>
                <p className="text-xs text-muted-foreground">
                  Permanently remove your account and all associated data
                </p>
              </div>
              <Button variant="danger" size="sm" iconName="Trash2" iconPosition="left">
                Delete
              </Button>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-4 border-t border-border">
          <Button
            onClick={handleSaveSettings}
            loading={isLoading}
            iconName="Shield"
            iconPosition="left"
          >
            Save Privacy Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyControls;
